import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Space, DatePicker } from 'antd';
import { supabase } from '../../supabaseClient';
import dayjs from 'dayjs';

const DeviceReleaseList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [dvcId, setDvcId] = useState('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [userEmailMap, setUserEmailMap] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string | undefined>();

  const fetchList = async () => {
    setLoading(true);
    let query = supabase.from('u_device_duties').select('*').order('created_at', { ascending: false });
    if (userId) query = query.eq('user_id', userId);
    if (userEmail) {
      // 先查邮箱对应的user_id
      const { data: users } = await supabase.from('u_profiles').select('user_id,email').ilike('email', `%${userEmail}%`);
      const userIds = (users || []).map((u: any) => u.user_id);
      if (userIds.length > 0) {
        query = query.in('user_id', userIds);
      } else {
        setData([]);
        setUserEmailMap({});
        setLoading(false);
        return;
      }
    }
    if (dvcId) query = query.eq('dvcId', dvcId);
    if (dateRange && dateRange[0]) query = query.gte('date', dateRange[0]);
    if (dateRange && dateRange[1]) query = query.lte('date', dateRange[1]);
    const { data: list, error } = await query;
    // 查询所有涉及到的user_id的邮箱
    const userIds = (list || []).map((item: any) => item.user_id);
    let emailMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: users } = await supabase.from('u_profiles').select('user_id,email').in('user_id', userIds);
      (users || []).forEach((u: any) => { emailMap[u.user_id] = u.email; });
    }
    // 状态筛选（前端判断）
    let filteredList = list || [];
    if (status) {
      filteredList = filteredList.filter((item: any) => {
        const isSettled = item.status === 'done' || Number(item.amount) === 0;
        return status === 'done' ? isSettled : !isSettled;
      });
    }
    setUserEmailMap(emailMap);
    setData(filteredList);
    setLoading(false);
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, []);

  const columns = [
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, ellipsis: true, render: (text: string) => text ? new Date(text).toLocaleString('zh-CN', { hour12: false }) : '' },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 320, ellipsis: true },
    { title: '用户邮箱', dataIndex: 'user_email', key: 'user_email', width: 200, ellipsis: true, render: (_: any, record: any) => userEmailMap[record.user_id] || '' },
    { title: '设备编号', dataIndex: 'dvcId', key: 'dvcId', width: 180, ellipsis: true },
    { title: '产出算力', dataIndex: 'amount', key: 'amount', width: 120, ellipsis: true },
    { title: '释放日期', dataIndex: 'date', key: 'date', width: 120, ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', width: 100, ellipsis: true, render: (_: string, record: any) => (record.status === 'done' || Number(record.amount) === 0) ? '已结算' : '未结算' },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 100, ellipsis: true },
  ];

  return (
    <div style={{ overflowX: 'auto', background: '#fff', padding: 12 }}>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }} size={[8, 8]}>
        <Input
          style={{ width: 200 }}
          placeholder="用户ID"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 200 }}
          placeholder="用户邮箱"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
          allowClear
        />
        <Input
          style={{ width: 200 }}
          placeholder="设备编号"
          value={dvcId}
          onChange={e => setDvcId(e.target.value)}
          allowClear
        />
        <DatePicker.RangePicker
          style={{ width: 260 }}
          placeholder={['开始', '结束']}
          value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
          onChange={dates => setDateRange(
            dates && dates[0] && dates[1]
              ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
              : null
          )}
          allowClear
        />
        <select
          style={{ width: 120, height: 32, borderRadius: 4, border: '1px solid #d9d9d9' }}
          value={status || ''}
          onChange={e => setStatus(e.target.value || undefined)}
        >
          <option value="">全部状态</option>
          <option value="notdone">未结算</option>
          <option value="done">已结算</option>
        </select>
        <Button type="primary" onClick={fetchList} loading={loading}>筛选</Button>
        <Button onClick={() => { setUserId(''); setUserEmail(''); setDvcId(''); setDateRange(null); setStatus(undefined); fetchList(); }}>重置</Button>
      </Space>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered
        pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (total) => `共 ${total} 条数据`, position: ['bottomRight'] }}
        scroll={{ x: 1200 }}
        size="small"
        style={{ minWidth: 900 }}
      />
    </div>
  );
};

export default DeviceReleaseList;
